import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkFrontmatter from 'remark-frontmatter';
import rehypeSanitize from 'rehype-sanitize';
import matter from 'gray-matter';

const MarkdownPage = ({ filePath: providedFilePath, title, basePath = '/documents' }) => {
  const params = useParams();
  const derivedPath = params && params.docName ? `${basePath}/${params.docName}.md` : undefined;
  const filePath = providedFilePath || derivedPath;

  const [content, setContent] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    let isCancelled = false;
    async function loadMarkdown() {
      if (!filePath) return;
      setIsLoading(true);
      setErrorMessage('');
      try {
        const publicUrl = (typeof process !== 'undefined' && process.env && process.env.PUBLIC_URL) ? process.env.PUBLIC_URL : '';
        let finalUrl = filePath;
        // If absolute path, prefix PUBLIC_URL; if relative, also prefix PUBLIC_URL
        if (!/^https?:\/\//i.test(finalUrl)) {
          if (finalUrl.startsWith('/')) {
            finalUrl = `${publicUrl}${finalUrl}`;
          } else {
            const normalized = finalUrl.replace(/^\/+/, '');
            finalUrl = `${publicUrl}/${normalized}`;
          }
        }
        const response = await fetch(finalUrl, { cache: 'no-cache' });
        if (!response.ok) {
          throw new Error(`Failed to fetch ${finalUrl} (${response.status})`);
        }
        const text = await response.text();
        let body = '';
        try {
          const parsed = matter(text);
          body = parsed && typeof parsed.content === 'string' ? parsed.content : text;
        } catch (_e) {
          body = text;
        }
        if (!isCancelled) {
          setContent(body);
        }
      } catch (error) {
        if (!isCancelled) {
          setErrorMessage('Unable to load this document. Please try again later.');
        }
      } finally {
        if (!isCancelled) {
          setIsLoading(false);
        }
      }
    }
    loadMarkdown();
    return () => {
      isCancelled = true;
    };
  }, [filePath]);

  return (
    <div className="max-w-3xl mx-auto px-6 py-10 text-left">
      {title ? (
        <h1 className="text-3xl font-bold mb-6">{title}</h1>
      ) : null}
      {isLoading ? (
        <div>Loading…</div>
      ) : errorMessage ? (
        <div className="text-red-600">{errorMessage}</div>
      ) : (
        <div className="prose max-w-none">
          <ReactMarkdown remarkPlugins={[remarkGfm, remarkFrontmatter, () => (tree) => { if (Array.isArray(tree.children)) { tree.children = tree.children.filter((node) => node.type !== 'yaml'); } }]} rehypePlugins={[rehypeSanitize]}>
            {content}
          </ReactMarkdown>
        </div>
      )}
    </div>
  );
};

export default MarkdownPage;


